import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Terminal, Search, Play, RefreshCw, CheckCircle, XCircle, Clock,
  Monitor, ChevronRight, ChevronLeft, AlertCircle, Loader2, X
} from 'lucide-react';
import { toast } from 'sonner';

export default function BulkScriptExecution({ open, onOpenChange }) {
  const [step, setStep] = useState(1); // 1=devices, 2=script, 3=results
  const [ninjaAssets, setNinjaAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [selectedDevices, setSelectedDevices] = useState(new Set());
  const [scripts, setScripts] = useState([]);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [scriptSearch, setScriptSearch] = useState('');
  const [selectedScript, setSelectedScript] = useState(null);
  const [scriptParams, setScriptParams] = useState('');
  const [runAs, setRunAs] = useState('SYSTEM');
  const [executing, setExecuting] = useState(false);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJob, setBulkJob] = useState(null);
  const [recentJobs, setRecentJobs] = useState([]);
  const pollingRef = useRef(null);

  useEffect(() => {
    if (open) {
      loadAssets();
      loadRecentJobs();
    }
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [open]);

  const loadAssets = async () => {
    setAssetsLoading(true);
    try {
      const resp = await api.get('/ninja-remote/ninja-assets');
      setNinjaAssets(resp.data || []);
    } catch { setNinjaAssets([]); }
    finally { setAssetsLoading(false); }
  };

  const loadScripts = async () => {
    if (scripts.length > 0) return;
    setScriptsLoading(true);
    try {
      const resp = await api.get('/ninja-remote/scripts-library');
      setScripts(Array.isArray(resp.data) ? resp.data : []);
    } catch { setScripts([]); }
    finally { setScriptsLoading(false); }
  };

  const loadRecentJobs = async () => {
    try {
      const resp = await api.get('/ninja-remote/bulk-jobs?limit=5');
      setRecentJobs(resp.data || []);
    } catch { /* silent */ }
  };

  const handleSelectAll = () => {
    const filtered = filteredAssets;
    if (selectedDevices.size === filtered.length) {
      setSelectedDevices(new Set());
    } else {
      setSelectedDevices(new Set(filtered.map(a => parseInt(a.ninjarmm_device_id))));
    }
  };

  const toggleDevice = (deviceId) => {
    const next = new Set(selectedDevices);
    if (next.has(deviceId)) next.delete(deviceId);
    else next.add(deviceId);
    setSelectedDevices(next);
  };

  const handleExecute = async () => {
    if (selectedDevices.size === 0) { toast.error('Select at least one device'); return; }
    if (!selectedScript) { toast.error('Select a script'); return; }
    setExecuting(true);
    try {
      const isAction = selectedScript.type === 'ACTION';
      const resp = await api.post('/ninja-remote/bulk-execute', {
        device_ids: Array.from(selectedDevices),
        script_type: isAction ? 'ACTION' : 'SCRIPT',
        script_id: isAction ? undefined : selectedScript.id,
        script_uid: isAction ? selectedScript.uid : undefined,
        script_name: selectedScript.name || '',
        script_content: scriptParams,
        run_as: runAs,
      });
      const jobId = resp.data.job_id;
      setBulkJobId(jobId);
      setStep(3);
      toast.success(`Bulk execution started on ${resp.data.total_devices} devices`);
      // Start polling
      startPolling(jobId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const startPolling = (jobId) => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const resp = await api.get(`/ninja-remote/bulk-jobs/${jobId}/poll`);
        setBulkJob(resp.data);
        const allDone = (resp.data.devices || []).every(d => ['completed', 'failed', 'submitted'].includes(d.status));
        if ((resp.data.status === 'completed' && allDone) || attempts >= 60) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
          if (attempts >= 60) toast.info('Polling stopped — refresh manually for updates');
        }
      } catch { /* continue */ }
    };
    poll();
    pollingRef.current = setInterval(poll, 3000);
  };

  const viewJob = async (jobId) => {
    setBulkJobId(jobId);
    setStep(3);
    try {
      const resp = await api.get(`/ninja-remote/bulk-jobs/${jobId}`);
      setBulkJob(resp.data);
    } catch { toast.error('Failed to load job'); }
  };

  const resetWizard = () => {
    setStep(1);
    setSelectedDevices(new Set());
    setSelectedScript(null);
    setScriptParams('');
    setBulkJobId(null);
    setBulkJob(null);
    if (pollingRef.current) { clearInterval(pollingRef.current); pollingRef.current = null; }
  };

  const filteredAssets = ninjaAssets.filter(a =>
    !deviceSearch || (a.name || '').toLowerCase().includes(deviceSearch.toLowerCase()) ||
    (a.customer_name || '').toLowerCase().includes(deviceSearch.toLowerCase())
  );

  const filteredScripts = scripts.filter(s =>
    !scriptSearch || (s.name || '').toLowerCase().includes(scriptSearch.toLowerCase()) ||
    (s.language || '').toLowerCase().includes(scriptSearch.toLowerCase())
  );

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-500" />;
      case 'running': return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'submitted': return <Clock className="h-4 w-4 text-amber-500" />;
      case 'queued': return <Clock className="h-4 w-4 text-gray-400" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'running': return 'bg-blue-100 text-blue-800';
      case 'submitted': return 'bg-amber-100 text-amber-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="bulk-execute-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Terminal className="h-5 w-5" />
            Bulk Script Execution
            {step < 3 && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                Step {step} of 2
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 pb-3 border-b">
          {[
            { n: 1, label: 'Select Devices' },
            { n: 2, label: 'Select Script' },
            { n: 3, label: 'Results' },
          ].map(({ n, label }) => (
            <div key={n} className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full ${
              step === n ? 'bg-gray-900 text-white' : step > n ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
            }`}>
              {step > n ? <CheckCircle className="h-3 w-3" /> : <span className="font-semibold">{n}</span>}
              {label}
            </div>
          ))}
        </div>

        {/* Step 1: Select Devices */}
        {step === 1 && (
          <div className="space-y-3" data-testid="step-devices">
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={deviceSearch}
                  onChange={(e) => setDeviceSearch(e.target.value)}
                  placeholder="Search devices by name or customer..."
                  className="pl-9 h-9"
                  data-testid="device-search-input"
                />
              </div>
              <Button size="sm" variant="outline" onClick={handleSelectAll} data-testid="select-all-btn">
                {selectedDevices.size === filteredAssets.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Badge variant="outline" className="whitespace-nowrap">
                {selectedDevices.size} selected
              </Badge>
            </div>

            {assetsLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading devices...
              </div>
            ) : (
              <div className="max-h-[400px] overflow-y-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="w-10 p-2"></th>
                      <th className="text-left p-2 font-medium text-gray-600">Device</th>
                      <th className="text-left p-2 font-medium text-gray-600">Customer</th>
                      <th className="text-left p-2 font-medium text-gray-600">Type</th>
                      <th className="text-left p-2 font-medium text-gray-600">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.map((asset) => {
                      const did = parseInt(asset.ninjarmm_device_id);
                      const isSelected = selectedDevices.has(did);
                      return (
                        <tr
                          key={asset.id}
                          onClick={() => toggleDevice(did)}
                          className={`cursor-pointer border-b transition-colors ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                          data-testid={`device-row-${did}`}
                        >
                          <td className="p-2 text-center">
                            <Checkbox checked={isSelected} onCheckedChange={() => toggleDevice(did)} />
                          </td>
                          <td className="p-2">
                            <div className="flex items-center gap-1.5">
                              <Monitor className="h-3.5 w-3.5 text-gray-400" />
                              <span className="font-medium">{asset.name}</span>
                            </div>
                          </td>
                          <td className="p-2 text-gray-600">{asset.customer_name || '-'}</td>
                          <td className="p-2"><Badge variant="outline" className="text-[10px]">{asset.type || 'device'}</Badge></td>
                          <td className="p-2">
                            <Badge variant="outline" className={`text-[10px] ${asset.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'}`}>
                              {asset.status || 'unknown'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {filteredAssets.length === 0 && (
                  <div className="text-center py-8 text-gray-500">No devices found</div>
                )}
              </div>
            )}

            {/* Recent bulk jobs */}
            {recentJobs.length > 0 && (
              <div className="pt-2 border-t">
                <p className="text-xs font-medium text-gray-500 mb-1.5">Recent Bulk Jobs</p>
                <div className="flex gap-2 flex-wrap">
                  {recentJobs.map(j => (
                    <button
                      key={j.id}
                      onClick={() => viewJob(j.id)}
                      className="text-xs px-2.5 py-1 rounded-md border bg-gray-50 hover:bg-gray-100 flex items-center gap-1.5"
                      data-testid={`recent-job-${j.id}`}
                    >
                      {getStatusIcon(j.status)}
                      <span className="font-medium">{j.script_name || 'Unnamed'}</span>
                      <span className="text-gray-400">({j.total_devices} devices)</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button onClick={() => { setStep(2); loadScripts(); }} disabled={selectedDevices.size === 0} data-testid="next-to-scripts-btn">
                Next: Select Script <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Select Script */}
        {step === 2 && (
          <div className="space-y-3" data-testid="step-scripts">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-xs text-blue-700 flex items-center gap-2">
              <Monitor className="h-4 w-4 shrink-0" />
              <span><strong>{selectedDevices.size} devices</strong> selected. Choose a script to execute on all of them.</span>
            </div>

            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={scriptSearch}
                  onChange={(e) => setScriptSearch(e.target.value)}
                  placeholder="Search scripts..."
                  className="pl-9 h-9"
                  data-testid="bulk-script-search"
                />
              </div>
              <Select value={runAs} onValueChange={setRunAs}>
                <SelectTrigger className="w-44 h-9" data-testid="bulk-runas-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SYSTEM">Run as System</SelectItem>
                  <SelectItem value="LOGGED_ON_USER">Run as User</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {scriptsLoading ? (
              <div className="flex items-center justify-center py-12 text-gray-500">
                <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading scripts...
              </div>
            ) : (
              <div className="max-h-[280px] overflow-y-auto space-y-1 border rounded-lg p-2" data-testid="bulk-scripts-list">
                {filteredScripts.map((script) => {
                  const key = script.uid || script.id;
                  const selKey = selectedScript?.uid || selectedScript?.id;
                  const isSelected = key && selKey && key === selKey;
                  return (
                    <div
                      key={key}
                      onClick={() => setSelectedScript(script)}
                      className={`flex items-center justify-between p-2 rounded-lg border cursor-pointer transition-all ${
                        isSelected ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200' : 'bg-white hover:bg-gray-50 border-gray-200'
                      }`}
                      data-testid={`bulk-script-${key}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium truncate">{script.name || `Script #${script.id}`}</span>
                        {script.type === 'ACTION' && <Badge variant="outline" className="text-[10px] shrink-0 bg-purple-50 text-purple-700 border-purple-200">Action</Badge>}
                        {script.language && <Badge variant="outline" className="text-[10px] shrink-0">{script.language}</Badge>}
                      </div>
                      {isSelected && <CheckCircle className="h-4 w-4 text-blue-500 shrink-0" />}
                    </div>
                  );
                })}
              </div>
            )}

            {selectedScript && (
              <div className="bg-gray-50 border rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-blue-500" />
                  <span className="text-sm font-semibold">{selectedScript.name}</span>
                </div>
                <div>
                  <Label className="text-xs text-gray-500 mb-1 block">Parameters (optional)</Label>
                  <Textarea
                    value={scriptParams}
                    onChange={(e) => setScriptParams(e.target.value)}
                    placeholder="Enter parameters to pass to the script..."
                    rows={2}
                    className="font-mono text-sm"
                    data-testid="bulk-params-input"
                  />
                </div>
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep(1)} data-testid="back-to-devices-btn">
                <ChevronLeft className="h-4 w-4 mr-1" /> Back
              </Button>
              <Button onClick={handleExecute} disabled={!selectedScript || executing} data-testid="bulk-execute-btn">
                <Play className="h-4 w-4 mr-2" />
                {executing ? 'Executing...' : `Execute on ${selectedDevices.size} Devices`}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Results Dashboard */}
        {step === 3 && bulkJob && (
          <div className="space-y-3" data-testid="step-results">
            {/* Summary bar */}
            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3 border">
              <div>
                <div className="flex items-center gap-2">
                  <Terminal className="h-4 w-4 text-gray-600" />
                  <span className="text-sm font-semibold">{bulkJob.script_name || 'Bulk Execution'}</span>
                  <Badge variant="outline" className={`text-[10px] ${getStatusColor(bulkJob.status)}`}>{bulkJob.status}</Badge>
                </div>
                <p className="text-xs text-gray-500 mt-0.5">
                  {bulkJob.total_devices} devices &middot; {bulkJob.executed_by} &middot; {bulkJob.created_at?.slice(0, 16).replace('T', ' ')}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Stats */}
                <div className="flex gap-2 text-xs">
                  <span className="flex items-center gap-1 text-green-600">
                    <CheckCircle className="h-3 w-3" />
                    {(bulkJob.devices || []).filter(d => d.status === 'completed' || d.status === 'submitted').length}
                  </span>
                  <span className="flex items-center gap-1 text-red-600">
                    <XCircle className="h-3 w-3" />
                    {(bulkJob.devices || []).filter(d => d.status === 'failed').length}
                  </span>
                  <span className="flex items-center gap-1 text-blue-600">
                    <Loader2 className="h-3 w-3" />
                    {(bulkJob.devices || []).filter(d => d.status === 'running' || d.status === 'queued').length}
                  </span>
                </div>
                <Button size="sm" variant="outline" onClick={() => startPolling(bulkJob.id)} data-testid="refresh-results-btn">
                  <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
                </Button>
              </div>
            </div>

            {/* Progress bar */}
            {(() => {
              const total = bulkJob.total_devices || 1;
              const done = (bulkJob.devices || []).filter(d => !['queued', 'running'].includes(d.status)).length;
              const pct = Math.round((done / total) * 100);
              return (
                <div className="w-full bg-gray-200 rounded-full h-2" data-testid="progress-bar">
                  <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                </div>
              );
            })()}

            {/* Per-device results */}
            <div className="max-h-[380px] overflow-y-auto border rounded-lg" data-testid="bulk-results-list">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2 font-medium text-gray-600">Device</th>
                    <th className="text-left p-2 font-medium text-gray-600">Status</th>
                    <th className="text-left p-2 font-medium text-gray-600">Output / Error</th>
                  </tr>
                </thead>
                <tbody>
                  {(bulkJob.devices || []).map((device, idx) => (
                    <tr key={idx} className="border-b" data-testid={`bulk-device-${device.device_id}`}>
                      <td className="p-2">
                        <div className="flex items-center gap-1.5">
                          <Monitor className="h-3.5 w-3.5 text-gray-400" />
                          <span className="font-medium">{device.asset_name || `Device ${device.device_id}`}</span>
                        </div>
                      </td>
                      <td className="p-2">
                        <div className="flex items-center gap-1.5">
                          {getStatusIcon(device.status)}
                          <Badge variant="outline" className={`text-[10px] ${getStatusColor(device.status)}`}>
                            {device.status}
                          </Badge>
                        </div>
                      </td>
                      <td className="p-2">
                        {device.output ? (
                          <pre className="font-mono text-[11px] text-gray-700 bg-gray-100 p-1.5 rounded max-h-[60px] overflow-y-auto whitespace-pre-wrap break-all">
                            {device.output}
                          </pre>
                        ) : device.error ? (
                          <span className="text-xs text-red-600">{device.error.slice(0, 120)}</span>
                        ) : (
                          <span className="text-xs text-gray-400">{device.status === 'queued' ? 'Waiting...' : device.status === 'running' ? 'Executing...' : '-'}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={resetWizard} data-testid="new-bulk-job-btn">
                New Bulk Execution
              </Button>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
            </div>
          </div>
        )}

        {step === 3 && !bulkJob && (
          <div className="flex items-center justify-center py-16 text-gray-500">
            <RefreshCw className="h-5 w-5 animate-spin mr-2" /> Loading job results...
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
